import React, {useEffect, useRef, useState} from "react";
import {Checkbox, FormControlLabel} from "@material-ui/core";
import DataAssetRunsTableComponent from "./data_asset_table";
import {DataAssetOverview} from "common/flowmaster/types/data_asset";
import {getEntityFromLocationSearch} from "routes/data_assets/util/get_entity_from_url";
import {getFiltersFromLocationSearch} from "routes/data_assets/util/get_dates_from_url";
import {OrganisationId} from "common/types/organisation";
import {DataKey} from "common/flowmaster/types/workflow";
import {TestId} from "common/flowmaster/types/test";
import {EnhancedLlmRun, LlmRunId} from "common/flowmaster/types/llm_run";
import {DocumentId} from "common/types/document";
import {ProjectId} from "common/types/project";
import {Definition} from "routes/data_assets/data_assets_create_view/components/data_assets_create_view";
import {RefreshWithFeedbackButton} from "routes/data_assets/util/refresh_with_feedback_button";
import OutputPreview from "./data_asset_table_output_preview";
import ComparisonLink from "routes/data_assets/util/comparison_link";
import TestListLink from "routes/data_assets/util/test_list_link";

interface DataAssetsOverviewProps {
  location: {
    pathname: string;
    search: string;
  };
  isLoading: boolean;
  isRefreshing: boolean;
  dataAssetRuns: DataAssetOverview[];
  fetchRuns: () => Promise<void>;
  fetchRunsError: string | null;
  organisationId: OrganisationId;
  onAddTest: (dataKey: DataKey, key: string) => void;
  onUpdateTest: (
    testId: TestId,
    testType: string,
    value: Record<string, unknown>,
  ) => void;
  onDeleteTest: (testId: TestId) => void;
  rerunTests: (runId: LlmRunId) => void;
  getTestResults: (dataKey: DataKey, key: string) => void;
  fetchLlmRun: (organisationId: OrganisationId, runId: LlmRunId) => void;
  selectedLlmRun: EnhancedLlmRun | null;
  filterByRunEntity: (projectId: ProjectId, documentId?: DocumentId) => void;
  updateFilterValue: (filters: object) => void;
  definitions: Definition[];
  onRefreshClick: () => void;
  isRerunningTests: boolean;
  isRefreshingRun: boolean;
}

const DataAssetsOverview: React.FC<DataAssetsOverviewProps> = ({
  location,
  isLoading,
  isRefreshing,
  dataAssetRuns,
  fetchRuns,
  fetchRunsError,
  onAddTest,
  onUpdateTest,
  onDeleteTest,
  rerunTests,
  getTestResults,
  fetchLlmRun,
  organisationId,
  selectedLlmRun,
  filterByRunEntity,
  updateFilterValue,
  definitions,
  onRefreshClick,
  isRerunningTests,
  isRefreshingRun,
}) => {
  const {
    projectId: initialProjId,
    documentId: initialDocId,
  } = getEntityFromLocationSearch(location.search);

  const initialFilters = getFiltersFromLocationSearch(location.search);

  const [documentId, setDocumentId] = useState<string | null>(initialDocId);
  const [projectId, setProjectId] = useState<string | null>(initialProjId);
  const [filters, setFilters] = useState(initialFilters);

  const [showOnlyTopLevelRuns, setShowOnlyTopLevelRuns] = useState(false);

  const refreshButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const {
      documentId: newDocumentId,
      projectId: newProjectId,
    } = getEntityFromLocationSearch(location.search);

    const docChanged = newDocumentId !== documentId;
    const projChanged = newProjectId !== projectId;

    if (newDocumentId !== documentId) {
      setDocumentId(newDocumentId);
    }
    if (newProjectId !== projectId) {
      setProjectId(newProjectId);
    }

    if (docChanged || projChanged) {
      fetchRuns();
    }
  }, [location.search]);

  useEffect(() => {
    const {from, to, user} = filters;
    const {
      from: newFrom,
      to: newTo,
      user: newUser,
    } = getFiltersFromLocationSearch(location.search);

    if (from !== newFrom || to !== newTo || user !== newUser) {
      setFilters({from: newFrom, to: newTo, user: newUser});
      fetchRuns();
    }
  }, [location.search]);

  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  const handleCloseOutput = () => {
    setSelectedRunId(null);
  };

  const handleOpenOutput = (run: DataAssetOverview) => {
    const runId = run.info.last_llm_run_id;

    if (selectedRunId === runId) {
      setSelectedRunId(null);
      return;
    }

    setSelectedRunId(runId);
    fetchLlmRun(organisationId, runId);
  };

  const selectedDataAssetRun = selectedRunId
    ? dataAssetRuns.find(run => run.info.last_llm_run_id === selectedRunId)
    : null;

  return (
    <>
      {selectedLlmRun?.id === selectedRunId && (
        <OutputPreview
          definition={(selectedDataAssetRun as DataAssetOverview).definition}
          config={(selectedDataAssetRun as DataAssetOverview).config}
          llmRun={selectedLlmRun}
          organisationId={organisationId}
          selectedProjectId={projectId}
          selectedDocumentId={documentId}
          addTest={onAddTest}
          updateTest={onUpdateTest}
          deleteTest={onDeleteTest}
          getTestResults={getTestResults}
          rerunTests={rerunTests}
          fetchLlmRun={fetchLlmRun}
          definitions={definitions}
          onClose={handleCloseOutput}
          ignoreClickawayRefs={[refreshButtonRef]}
          isRerunningTests={isRerunningTests}
          isRefreshing={isRefreshingRun}
        />
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1em",
          padding: "1em",
          flexGrow: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1em",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyTopLevelRuns}
                onChange={() => setShowOnlyTopLevelRuns(!showOnlyTopLevelRuns)}
                color="primary"
              />
            }
            label="Show only top level runs"
          />
          <ComparisonLink
            organisationId={organisationId}
            projectId={projectId}
            documentId={documentId}
            from={filters.from}
            to={filters.to}
          />
          <TestListLink
            organisationId={organisationId}
            projectId={projectId}
            documentId={documentId}
            from={filters.from}
            to={filters.to}
          />

          <RefreshWithFeedbackButton
            isLoading={isRefreshing}
            handleRefresh={onRefreshClick}
            reverse={true}
            ref={refreshButtonRef}
          />
        </div>
      </div>
      <DataAssetRunsTableComponent
        isLoading={isLoading}
        fetchRunsError={fetchRunsError}
        dataAssetRuns={dataAssetRuns}
        selectedDataAssetRun={selectedDataAssetRun}
        organisationId={organisationId}
        filterByRunEntity={filterByRunEntity}
        updateFilterValue={updateFilterValue}
        refreshButtonRef={refreshButtonRef}
        handleOpenOutput={handleOpenOutput}
        showOnlyTopLevelRuns={showOnlyTopLevelRuns}
      />
    </>
  );
};

export default DataAssetsOverview;
